import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { createCatsSupabaseShim, sapOwnedTableQuery, SAP_OWNED_TABLES } from './supabaseCatsShim'
// Single source of truth for the SAP-backend switch (was a local VITE_TS_BACKEND read).
import { IS_S4_BSP_BUILD, IS_HOSTED_SAP } from '@/config/sapBackend'
// Enterprise (Azure) build: data flows through the Entra-brokered PostgREST
// proxy instead of Supabase. See the entra branch in getSupabase().
import { isEntraAuthEnabled, acquireApiToken } from './entraAuth'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

/**
 * The S/4-hosted (Option A) build runs FULLY in-boundary — zero Supabase. When
 * `VITE_TS_BACKEND==='cats'` and no Supabase is configured, every
 * `supabase.from(<table>)` call is served by the in-boundary ABAP config store
 * shim (same-origin `/sap/bc/zm12cfg`) instead of reaching an external DB. This
 * lets the ~100 admin/config services keep their `supabase.from(...)` calls
 * unchanged while the app self-hosts on S/4. See lib/supabaseCatsShim.ts.
 */
const useCatsConfigStore = IS_S4_BSP_BUILD && !(supabaseUrl && supabaseAnonKey)

/**
 * Retry-with-backoff wrapper around the native fetch used by the Supabase
 * client. Defends against the two most common transient failures:
 *
 *   1. PostgREST schema-cache reload after a migration apply — every read
 *      against the affected schema 503s for ~15-30s while the cache rebuilds.
 *   2. Cold-start / wake-from-pause on Supabase free / dev tiers.
 *
 * Only idempotent methods (GET / HEAD) are retried — POST / PATCH / DELETE
 * may have partially landed on the server before the 503, so silently
 * retrying them risks duplicate writes. Mutations surface immediately.
 *
 * Retry on: 503 Service Unavailable, 504 Gateway Timeout, network failures
 * (fetch throws TypeError when the request never reached the server).
 *
 * Backoff: 200ms → 600ms → 1800ms (exponential, max 3 attempts total).
 */
const RETRY_DELAYS_MS = [200, 600, 1800] as const

/**
 * The request an anonymous public form page is submitting (migration 00535).
 * While set, every database call carries it as the x-wff-submission header,
 * which is how row security lets an anon caller read back and route that one
 * request (and nothing else). Set and cleared by the public form page only.
 */
let publicSubmissionRef: string | null = null

export function setPublicSubmissionRef(id: string | null): void {
  publicSubmissionRef = id
}

function withPublicSubmissionRef(init?: RequestInit): RequestInit | undefined {
  if (!publicSubmissionRef) return init
  const headers = new Headers(init?.headers)
  headers.set('x-wff-submission', publicSubmissionRef)
  return { ...init, headers }
}

async function fetchWithRetry(
  input: RequestInfo | URL,
  rawInit?: RequestInit,
): Promise<Response> {
  const init = withPublicSubmissionRef(rawInit)
  const method = (init?.method ?? 'GET').toUpperCase()
  const isIdempotent = method === 'GET' || method === 'HEAD'

  let lastError: unknown
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    try {
      const res = await fetch(input, init)
      // Only consider retry for idempotent requests with retryable status.
      if (!isIdempotent || (res.status !== 503 && res.status !== 504)) {
        return res
      }
      // Last attempt — surface the response (caller decides what to do).
      if (attempt === RETRY_DELAYS_MS.length) return res
      const delay = RETRY_DELAYS_MS[attempt]!
      const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url
      console.warn(`[supabase] ${res.status} on ${method} ${url} — retrying in ${delay}ms (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length})`)
      await new Promise((resolve) => window.setTimeout(resolve, delay))
    } catch (err) {
      // Network failure — same retry policy applies.
      lastError = err
      if (!isIdempotent || attempt === RETRY_DELAYS_MS.length) throw err
      const delay = RETRY_DELAYS_MS[attempt]!
      console.warn(`[supabase] fetch failed (${(err as Error).message}) — retrying in ${delay}ms (attempt ${attempt + 1}/${RETRY_DELAYS_MS.length})`)
      await new Promise((resolve) => window.setTimeout(resolve, delay))
    }
  }
  // Unreachable but TS doesn't know.
  throw lastError ?? new Error('fetchWithRetry: exhausted attempts')
}

/**
 * True when both Supabase build vars are present. The S/4-hosted (Option A)
 * build ships WITHOUT them, so this is `false` there and no client is ever
 * constructed. Callers that want to branch (rather than let access throw) can
 * check this first.
 */
export function isSupabaseConfigured(): boolean {
  // The in-boundary config store is a valid backing "database" for the S/4 build,
  // so callers that guard on this (and would otherwise bail with empty data)
  // proceed and hit the shim. The Entra-brokered Azure backend likewise counts
  // as configured — the client is constructed against /api/db.
  return Boolean(supabaseUrl && supabaseAnonKey) || useCatsConfigStore || isEntraAuthEnabled()
}

/**
 * Lazily construct the real Supabase client on first ACCESS — not at module
 * import. This is the Option A "no Supabase in the S/4 build" seam
 * (runbook Phase 1, step 4):
 *
 *  - The live app is unaffected: its vars are present, so the first property
 *    access builds exactly the same client (same `fetchWithRetry`) it built
 *    eagerly before, and it is cached for the app's lifetime.
 *  - The S/4 build omits the vars (they are blanked in vite.config.s4.ts and
 *    absent from .env.s4). Merely importing `supabase` — as
 *    timesheetSupabaseService and @/services/supabaseService both do at module
 *    load — constructs nothing and opens no connection. Only an *actual* query
 *    would try to build a client, and with the vars absent that throws locally
 *    instead of reaching the live database.
 */
let _client: SupabaseClient | null = null

export function getSupabase(): SupabaseClient {
  if (_client) return _client
  if (useCatsConfigStore) {
    // In-boundary S/4 build: serve every from(...) from the ABAP config store.
    _client = createCatsSupabaseShim() as unknown as SupabaseClient
    return _client
  }
  if (isEntraAuthEnabled()) {
    // Enterprise (Azure) build: same PostgREST dialect the app already speaks,
    // served by the xpm-api /api/db proxy (Entra bearer in, RLS-scoped
    // PostgREST JWT out, Azure PG behind it). supabase.auth.* is unavailable
    // in this mode (authStore's entra branches never call it).
    _client = createClient(`${window.location.origin}/api/db`, 'entra-brokered', {
      global: { fetch: fetchWithRetry },
      accessToken: async () => (await acquireApiToken()) ?? '',
    })
    return _client
  }
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY environment variables')
  }
  _client = createClient(supabaseUrl, supabaseAnonKey, {
    global: { fetch: fetchWithRetry },
  })
  return _client
}

/** Shifts kept in this database before the organization moved onto SAP. */
function hostedLegacyShifts(client: SupabaseClient): () => Promise<Record<string, unknown>[]> {
  return async () => {
    const { data, error } = await client.from('shifts').select('*')
    if (error) throw new Error(error.message)
    return (data ?? []) as Record<string, unknown>[]
  }
}

/**
 * Drop-in replacement for the previous eagerly-constructed `supabase` const.
 * A Proxy so the ~120 `import { supabase } from '@/lib/supabase'` call sites
 * (which only ever use member access — `supabase.from` / `.auth` / `.rpc` /
 * `.storage`) keep working verbatim, while construction is deferred to the
 * first property read via `getSupabase()`.
 */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop, receiver) {
    const client = getSupabase()
    // Hosted app with the timesheet on SAP CATS: the shift master and the shop
    // floor clock tables live in SAP (the ABAP clock engine checks against
    // them), so only those go there; every other table stays on this database.
    if (prop === 'from' && IS_HOSTED_SAP) {
      return (tbl: string) =>
        SAP_OWNED_TABLES.has(tbl)
          ? sapOwnedTableQuery(tbl, tbl === 'shifts' ? hostedLegacyShifts(client) : undefined)
          : client.from(tbl)
    }
    const value = Reflect.get(client as object, prop, receiver)
    return typeof value === 'function' ? value.bind(client) : value
  },
  set(_target, prop, value) {
    const client = getSupabase()
    return Reflect.set(client as object, prop, value)
  },
  has(_target, prop) {
    return Reflect.has(getSupabase() as object, prop)
  },
})
